import { IOrderUser, ITransOfferObject, OrderUserValidationConfig } from '~api/CartAPI/ICartApiSdk';
import ICartItem from '~domain/entities/CartItem/ICartItem';
import { PropsObject, typeConfigs } from '~api/utils/sanitizeData';

export interface ICartTotals {
    totalCost: number;
    formattedTotalCost: string;
    subTotal: number;
    formattedSubtotal: string;
    taxRate: string;
    isVatApplied: boolean;
    totalTax: number;
    formattedTotalTax: string;
    shippingTax: number;
    formattedShippingTax: string;
    giftwrapAppliedPrice: number;
    formattedGiftwrapAppliedPrice: string;
    donationPrice: number;
    hasSalesTax: boolean;
    discount: {
        isDiscounted: boolean;
        totalPrice: number;
        formattedTotalPrice: string;
        discountAmount: number;
        formattedDiscountAmount: string;
        subtotalWithoutDonation: number;
        offersSummary: ITransOfferObject[];
    };
    maxTotalItemLimit?: number;
    paymentInstallments?: ICartBuyNowPayLater;
    potentialLoyaltyPoints?: number;
    loyaltyRewardCostInPoints?: boolean;
    retailDeliveryFee: number;
}

export const CartTotalsValidationConfig: PropsObject = {
    totalCost: typeConfigs.number(),
    formattedTotalCost: typeConfigs.string(),
    subTotal: typeConfigs.number(),
    formattedSubtotal: typeConfigs.string(),
    taxRate: typeConfigs.string(),
    isVatApplied: typeConfigs.boolean(),
    totalTax: typeConfigs.number(),
    formattedTotalTax: typeConfigs.string(),
    shippingTax: typeConfigs.number(),
    formattedShippingTax: typeConfigs.string({ customDefault: '-' }),
    giftwrapAppliedPrice: typeConfigs.number(),
    formattedGiftwrapAppliedPrice: typeConfigs.string({ customDefault: '-' }),
    donationPrice: typeConfigs.number(),
    discount: {
        type: 'object',
        data: {
            isDiscounted: typeConfigs.boolean(),
            totalPrice: typeConfigs.number(),
            formattedTotalPrice: typeConfigs.string(),
            discountAmount: typeConfigs.number(),
            formattedDiscountAmount: typeConfigs.string(),
            subtotalWithoutDonation: typeConfigs.number()
        }
    },
    maxTotalItemLimit: typeConfigs.number({ customDefault: 50 }),
    potentialLoyaltyPoints: typeConfigs.number(),
    loyaltyRewardCostInPoints: typeConfigs.boolean(),
    retailDeliveryFee: typeConfigs.number()
};

export interface ICartMessage {
    key: string;
    text: string;
    severity: string;
    tags: string[];
    display_locations?: string[];
    className?: string;
}

export interface ICartOffer {
    offer_code: string;
    is_ended: number;
    benefits: [{ class: string; methods: string[]; set_method: string }];
    description?: string;
    promo_text_heading: string;
    associated_drupal_node_id: string;
    items_incart: number;
}

export interface ICartOffers {
    automatic?: ICartOffer[];
    manual?: ICartOffer[];
}

export interface ICartBuyNowPayLater {
    afterpay?: {
        enabled: boolean;
        min: number;
        max: number;
        cartIsEligible: boolean;
    };
    klarna?: {
        enabled: boolean;
        min: number;
        max: number;
        cartIsEligible: boolean;
        installments: number;
        contentNodeIds: string;
    };
    paypalpaylater?: {
        enabled: boolean;
        min: number;
        max: number;
        cartIsEligible: boolean;
        logoPosition: paypalLogoPosition;
        logoStyleType: paypalLogoStyle;
        textStyleColor: paypalThemeColor;
        sdkUrl: string;
    };
}

const CartBuyNowPayLaterValidationConfig: PropsObject = {
    afterpay: {
        type: 'object',
        data: {
            enabled: typeConfigs.boolean(),
            min: typeConfigs.number(),
            max: typeConfigs.number(),
            cartIsEligible: typeConfigs.boolean()
        }
    },
    klarna: {
        type: 'object',
        data: {
            enabled: typeConfigs.boolean(),
            min: typeConfigs.number(),
            max: typeConfigs.number(),
            cartIsEligible: typeConfigs.boolean(),
            installments: typeConfigs.number(),
            contentNodeIds: typeConfigs.string()
        }
    },
    paypalpaylater: {
        type: 'object',
        data: {
            enabled: typeConfigs.boolean(),
            min: typeConfigs.number(),
            max: typeConfigs.number(),
            cartIsEligible: typeConfigs.boolean(),
            sdkUrl: typeConfigs.string()
        }
    }
};

export interface ICartOrderData {
    messages?: ICartMessage[];
    offers?: ICartOffers;
    user?: IOrderUser;
    paymentInstallments?: ICartBuyNowPayLater;
    is_giftWrap?: boolean | number;
    SHIP_METHOD?: number;
    wp_samples?: ICartOffer[];
    pwp?: ICartOffer[];
    ADDRESS_ID?: number;
}

export const CartOrderDataValidationConfig: PropsObject = {
    user: {
        type: 'object',
        data: OrderUserValidationConfig
    },
    paymentInstallments: {
        type: 'object',
        data: CartBuyNowPayLaterValidationConfig
    },
    is_giftWrap: typeConfigs.boolean(),
    SHIP_METHOD: typeConfigs.number(),
    ADDRESS_ID: typeConfigs.number()
};

export interface ICartProps {
    cartItems: ICartItem[];
    cartTotals: ICartTotals;
    cartOrderData?: ICartOrderData;
}

export default interface ICart {
    cartItems: ICartItem[];
    cartOrderData?: ICartOrderData;
    totalPrice: number;
    totalQty: number;
    samplesQty: number;
    pricedSkusQty: number;
    loyaltySkusQty: number;
}
